import { ApolloError, OperationVariables, QueryHookOptions } from '@apollo/client';
import { QueryResult } from '@apollo/client/react/types/types';
import { useMemo } from 'react';
import {
  ActivateTransactionFilter,
  MembershipFilter,
  MembershipItemFragment,
  MembershipListQuery,
  MembershipListQueryVariables,
  MembershipOfferFragment,
  MembershipOffersQuery,
  MembershipOffersQueryVariables,
  MembershipTransactionsQuery,
  MembershipTransactionsQueryVariables,
  MembershipVoucherFragment,
  MembershipVouchersQuery,
  MembershipVouchersQueryVariables,
  OfferFilter,
  SegmentForMembershipFilter,
  TransactionItemFragment,
  VoucherFilter,
  useAudiencesForMembershipQuery,
  useLocationsQuery,
  useMembershipListQuery,
  useMembershipOffersQuery,
  useMembershipQuery,
  useMembershipTransactionsQuery,
  useMembershipVouchersQuery,
  useSegmentsByMembershipUuidQuery,
  useAudiencesForMembershipWithPaginationQuery,
  AudienceForMembershipFilter,
  AudiencesForMembershipWithPaginationQuery,
  AudiencesForMembershipWithPaginationQueryVariables,
  VoucherSort,
  useMembershipCommentsQuery,
  MembershipCommentFragment,
  MembershipProfileCommentFilter,
  ActivateTransactionSort,
  RetailerNetworkLocationsForMemberQueryVariables,
  useRetailerNetworkLocationsForMemberQuery,
  RetailerNetworkLocationsForMemberQuery,
  RetailerNetworkLocationGroupsForMemberQueryVariables,
  useRetailerNetworkLocationGroupsForMemberQuery,
  RetailerNetworkLocationGroupsForMemberQuery,
  RetailerNetworkLocationGroupForMemberFragment,
  RetailerNetworkLocationForMemberFragment,
  useRetailerNetworksForMemberQuery,
  RetailerNetworksForMemberQueryVariables,
  RetailerNetworkForMemberFragment,
  RetailerNetworksForMemberQuery,
  RetailerNetworkUsersForMemberFilter,
  useRetailerNetworkUsersForMemberQuery,
  RetailerNetworkUsersForMemberQuery,
  RetailerNetworkUsersQueryVariables,
  RetailerNetworkUserForMemberFragment,
  SegmentByMembershipUuidFragment,
  useMembershipActivitiesQuery,
  MembershipActivitiesQueryVariables,
  MembershipActivityFilter,
  MembershipActivityFragment,
  MembershipActivitiesQuery,
} from '@occc/graphql';
import {
  IPaginationProps,
  PAGE_SIZE,
  PaginationHandler,
  PaginationInfo,
  usePaginatedQuery,
} from '@occc-app/hooks/usePaginatedQuery';
import { useChainId } from '@occc-app/Chain/hooks';
import { compareDesc } from '@occc-app/lib/date';

type Options<T, U extends OperationVariables> = QueryHookOptions<T[] | undefined, U>;

type MembershipOffersOptions = Options<MembershipOfferFragment, MembershipOffersQueryVariables>;
type MembershipVouchersOptions = Options<
  MembershipVoucherFragment,
  MembershipVouchersQueryVariables
>;

type GetMembershipOffers = {
  membershipId: string;
  filter?: OfferFilter;
  pageSize?: number;
  baseOptions?: MembershipOffersOptions;
  paginationInfo?: PaginationInfo;
  onPageChange?: PaginationHandler;
};

type GetMembershipVouchers = {
  membershipId: string;
  filter?: VoucherFilter;
  sort?: VoucherSort;
  pageSize?: number;
  baseOptions?: MembershipVouchersOptions;
  paginationInfo?: PaginationInfo;
  onPageChange?: PaginationHandler;
};

type GetMembershipAudiences = {
  filter?: AudienceForMembershipFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    AudiencesForMembershipWithPaginationQuery,
    AudiencesForMembershipWithPaginationQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: PaginationHandler;
};

export const useGetMembershipById = (idEquals: string) => {
  const chainId = useChainId();
  const filter: MembershipFilter = {
    idEquals,
    chainIdEquals: chainId,
  };

  const membershipResponse = useMembershipQuery({
    notifyOnNetworkStatusChange: true,
    variables: { filter },
  });

  const node = membershipResponse?.data?.memberships?.edges?.[0]?.node;

  const stateChanges = node?.stateChanges.map((item) => item) || [];
  const sortedStateChanges = stateChanges.sort((prevItem, nextItem) => {
    if (!prevItem?.effectiveAt || !nextItem?.effectiveAt) {
      return NaN;
    }
    return compareDesc(prevItem.effectiveAt, nextItem.effectiveAt);
  });

  const membershipData = node ? { ...node, stateChanges: sortedStateChanges } : undefined;

  return {
    ...membershipResponse,
    data: membershipData,
  };
};

const mapMembershipOffersResponseToData: (
  data?: MembershipOffersQuery,
) => MembershipOfferFragment[] | undefined = (data) => {
  return data?.memberships?.edges?.[0]?.node?.offers?.edges?.map((e) => e.node);
};

export const useGetMembershipOffers = ({
  membershipId,
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetMembershipOffers) => {
  const response = usePaginatedQuery(
    useMembershipOffersQuery,
    'memberships.edges[0].node.offers.pageInfo',
    pageSize,
    {
      variables: {
        membershipId,
        filter,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(mapMembershipOffersResponseToData(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: mapMembershipOffersResponseToData(response.data),
  };
};

const mapMembershipVouchersResponseToData: (
  data?: MembershipVouchersQuery,
) => MembershipVoucherFragment[] | undefined = (data) => {
  return data?.memberships?.edges?.[0]?.node?.vouchers?.edges?.map((e) => e.node);
};

export const useGetMembershipVouchers = ({
  membershipId,
  filter,
  sort,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetMembershipVouchers) => {
  const response = usePaginatedQuery(
    useMembershipVouchersQuery,
    'memberships.edges[0].node.vouchers.pageInfo',
    pageSize,
    {
      variables: {
        membershipId,
        filter,
        sort,
      },
      notifyOnNetworkStatusChange: true,
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(mapMembershipVouchersResponseToData(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: mapMembershipVouchersResponseToData(response.data),
  };
};

export const useGetMemberPreferredStore = (locationId?: string | null): QueryResult => {
  const response = useLocationsQuery({
    variables: {
      filter: {
        uuidEquals: locationId,
      },
    },
    skip: !locationId,
  });

  const data =
    response?.data?.locations?.edges?.[0]?.node?.siteSystemConfiguration?.[0]?.details?.siteName ||
    '';

  return {
    ...response,
    data,
  };
};

export const useGetAudiencesForMembership = (membershipId: string) => {
  const response = useAudiencesForMembershipQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !membershipId,
    variables: {
      membershipId,
    },
  });

  return {
    ...response,
    data: response.data?.audiencesForMembership || [],
  };
};

export const useGetAudiencesForMembershipWithPagination = ({
  pageSize,
  paginationInfo,
  onPageChange,
  filter,
}: GetMembershipAudiences) => {
  const response = usePaginatedQuery(
    useAudiencesForMembershipWithPaginationQuery,
    'audiencesForMembershipWithPagination.pageInfo',
    pageSize,
    {
      variables: {
        filter,
      },
    },
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data?.audiencesForMembershipWithPagination.edges.map((edge) => edge.node),
  };
};

export type UseGetSegmentsByMembershipUuidParams = {
  filter: SegmentForMembershipFilter;
  pageSize?: number;
  skip?: boolean;
};

export type UseGetSegmentsByMembershipUuidReturns = {
  loading: boolean;
  data: SegmentByMembershipUuidFragment[];
  error?: ApolloError;
  paginationProps: IPaginationProps;
};

export const useGetSegmentsByMembershipUuid = ({
  filter,
  pageSize,
  skip = false,
}: UseGetSegmentsByMembershipUuidParams): UseGetSegmentsByMembershipUuidReturns => {
  const response = usePaginatedQuery(
    useSegmentsByMembershipUuidQuery,
    'segmentsByMembershipUuid.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      skip,
    },
  );

  const data: SegmentByMembershipUuidFragment[] =
    response.data?.segmentsByMembershipUuid?.edges.map((e) => e.node) || [];

  return {
    ...response,
    data,
  };
};

type GetMembershipsProps = {
  filter?: MembershipFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    MembershipItemFragment[] | undefined,
    MembershipListQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: PaginationHandler;
};

function mapResponseToData(data?: MembershipListQuery): MembershipItemFragment[] | undefined {
  return data?.memberships?.edges.map((e) => e.node);
}

export function useGetMemberships({
  filter,
  pageSize = PAGE_SIZE,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetMembershipsProps) {
  const response = usePaginatedQuery(
    useMembershipListQuery,
    'memberships.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions?.onCompleted) {
          baseOptions.onCompleted(mapResponseToData(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: mapResponseToData(response.data),
  };
}

type MembershipTransactionsOptions = Options<
  TransactionItemFragment,
  MembershipTransactionsQueryVariables
>;

type GetMembershipTransactionsProps = {
  membershipId: string;
  filter?: ActivateTransactionFilter;
  sort?: ActivateTransactionSort;
  pageSize?: number;
  baseOptions?: MembershipTransactionsOptions;
  paginationInfo?: PaginationInfo;
  onPageChange?: PaginationHandler;
};

export const useGetMembershipTransactions = ({
  membershipId,
  filter,
  sort,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetMembershipTransactionsProps) => {
  const mapResponseToData: (
    data?: MembershipTransactionsQuery,
  ) => TransactionItemFragment[] | undefined = (data) => {
    return data?.memberships?.edges?.[0]?.node?.transactions?.edges?.map((e) => e.node);
  };

  const response = usePaginatedQuery(
    useMembershipTransactionsQuery,
    'memberships.edges[0].node.transactions.pageInfo',
    pageSize,
    {
      variables: {
        membershipId,
        filter,
        sort,
      },
      notifyOnNetworkStatusChange: true,
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(mapResponseToData(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: mapResponseToData(response.data),
  };
};

type UseMembershipCommentsParams = {
  membershipId: string;
  filter?: MembershipProfileCommentFilter;
  paginationInfo: PaginationInfo;
  onPageChange: PaginationHandler;
};

export const useMembershipComments = (params: UseMembershipCommentsParams) => {
  const { membershipId, filter = null, paginationInfo, onPageChange } = params;

  const { data, loading, error, paginationProps } = usePaginatedQuery(
    useMembershipCommentsQuery,
    'memberships.edges[0].node.comments.pageInfo',
    undefined, // please use paginationInfo instead
    {
      variables: {
        membershipId,
        filter,
      },
    },
    paginationInfo,
    onPageChange,
  );

  const comments = useMemo<MembershipCommentFragment[]>(
    () => data?.memberships?.edges?.[0]?.node.comments.edges.map(({ node }) => node) ?? [],
    [data],
  );
  return { comments, loading, error, paginationProps };
};

type GetRetailerNetworkLocationsForMemberProps = {
  memberUuid: string;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkLocationForMemberFragment[],
    RetailerNetworkLocationsForMemberQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
  skip?: boolean;
};

const transformDataToRetailerNetworkLocationsForMemberArray = (
  data: RetailerNetworkLocationsForMemberQuery,
): RetailerNetworkLocationForMemberFragment[] => {
  const edges = data.retailerNetworkLocationsForMember?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkLocationsForMember = ({
  memberUuid,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
  skip,
}: GetRetailerNetworkLocationsForMemberProps) => {
  const response = usePaginatedQuery(
    useRetailerNetworkLocationsForMemberQuery,
    'retailerNetworkLocationsForMember.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip,
      variables: {
        memberUuid,
      },
      ...baseOptions,
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data
      ? transformDataToRetailerNetworkLocationsForMemberArray(response.data)
      : response.data,
  };
};

type GetRetailerNetworkLocationGroupsForMemberProps = {
  memberUuid: string;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkLocationGroupForMemberFragment[],
    RetailerNetworkLocationGroupsForMemberQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
  skip?: boolean;
};

const transformDataToRetailerNetworkLocationGroupsForMemberArray = (
  data: RetailerNetworkLocationGroupsForMemberQuery,
): RetailerNetworkLocationGroupForMemberFragment[] => {
  const edges = data.retailerNetworkLocationGroupsForMember?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkLocationGroupsForMember = ({
  memberUuid,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
  skip,
}: GetRetailerNetworkLocationGroupsForMemberProps) => {
  const response = usePaginatedQuery(
    useRetailerNetworkLocationGroupsForMemberQuery,
    'retailerNetworkLocationGroupsForMember.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip,
      variables: {
        memberUuid,
      },
      ...baseOptions,
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data
      ? transformDataToRetailerNetworkLocationGroupsForMemberArray(response.data)
      : response.data,
  };
};

type GetRetailerNetworksForMemberProps = {
  memberUuid: string;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkForMemberFragment[],
    RetailerNetworksForMemberQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
  skip?: boolean;
};

const transformDataToRetailerNetworkForMemberArray = (
  data: RetailerNetworksForMemberQuery,
): RetailerNetworkForMemberFragment[] => {
  const edges = data.retailerNetworksForMember?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworksForMember = ({
  memberUuid,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
  skip,
}: GetRetailerNetworksForMemberProps) => {
  const response = usePaginatedQuery(
    useRetailerNetworksForMemberQuery,
    'retailerNetworksForMember.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip,
      variables: {
        memberUuid,
      },
      ...baseOptions,
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data
      ? transformDataToRetailerNetworkForMemberArray(response.data)
      : response.data,
  };
};

type GetRetailerNetworkUsersForMemberProps = {
  filter: RetailerNetworkUsersForMemberFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkUserForMemberFragment[],
    RetailerNetworkUsersQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
  skip?: boolean;
};

const transformDataToRetailerNetworkUsersForMemberArray = (
  data: RetailerNetworkUsersForMemberQuery,
): RetailerNetworkUserForMemberFragment[] => {
  const edges = data.retailerNetworkUsersForMember?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkUsersForMemberQuery = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
  skip,
}: GetRetailerNetworkUsersForMemberProps) => {
  const response = usePaginatedQuery(
    useRetailerNetworkUsersForMemberQuery,
    'retailerNetworkUsersForMember.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip,
      variables: {
        filter,
      },
      ...baseOptions,
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data
      ? transformDataToRetailerNetworkUsersForMemberArray(response.data)
      : response.data,
  };
};

type GetMembershipActivitiesProps = {
  membershipId: string;
  filter: MembershipActivityFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<MembershipActivityFragment[], MembershipActivitiesQueryVariables>;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

const isMembershipActivity = (
  value: MembershipActivityFragment | null | undefined,
): value is MembershipActivityFragment => value != null;

const mapMembershipActivitiesResponseToData = (
  data?: MembershipActivitiesQuery,
): MembershipActivityFragment[] => {
  const activityEdges = data?.memberships?.edges?.[0]?.node?.membershipActivities?.edges ?? [];
  return activityEdges.map((edge) => edge?.node).filter(isMembershipActivity);
};

export const useGetMembershipActivities = ({
  membershipId,
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetMembershipActivitiesProps) => {
  const response = usePaginatedQuery(
    useMembershipActivitiesQuery,
    'memberships.edges[0].node.membershipActivities.pageInfo',
    pageSize,
    {
      variables: {
        membershipId,
        filter,
      },
      notifyOnNetworkStatusChange: true,
      ...baseOptions,
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: mapMembershipActivitiesResponseToData(response.data),
  };
};
